import {Component} from "@angular/core";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {DialogComponent} from "../bootstrap-modal/dialog.component";
import "rxjs/add/operator/toPromise";
import {handleErrors} from "../misc/handle-errors.decorator";
import {SearchHelpers} from "../helpers/search-helper";
import {AssetService} from "./asset.service";
import {AccessoryService} from "../accessory/accessory.service";
import {AssetForSiteDesignModel} from "./asset-for-site-design.model";

export interface SelectAssetAssociationDialogModel {
  typeModal: string;
  siteId: string;
  solution: AssetForSiteDesignModel;
  assets: AssetForSiteDesignModel[];
}

interface AssetSelectedDataModel {
  id: string;
  description: string;
}

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 5;

@Component({
  templateUrl: "./select-asset-association-dialog.template.pug",
})
export class SelectAssetAssociationDialogComponent
  extends DialogComponent<SelectAssetAssociationDialogModel, any> implements SelectAssetAssociationDialogModel {
  typeModal: string = "";
  siteId: string;
  solution: AssetForSiteDesignModel;
  loading: boolean = false;
  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number;
  totalCount: number = 0;
  searchText: string = "";
  assetsFiltered: AssetForSiteDesignModel[] = [];
  assetsDisplay: AssetForSiteDesignModel[] = [];
  assets: AssetForSiteDesignModel[] = [];
  assetSelectedData: AssetSelectedDataModel = {id: "", description: ""};
  selected: boolean = false;
  public error: Error;

  cancel(): void {
    this.result = {};
    this.close();
  }

  async ready() {
    this.loadAssetsToDisplay();
  }

  private loadAssetsToDisplay() {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;

    this.totalCount = this.assets.length;
    this.assetsFiltered = this.assets.filter((asset) => {
      if (this.searchText) {
        const values = [
          asset.description,
        ];

        const searchFailures = SearchHelpers.tokenizeString(this.searchText) // split on spaces but consider "" and ''
          .map(v => SearchHelpers.stripQuotes(v)) // strip quotes on ends if present
          .map(v => SearchHelpers.cleanParam(v)) // sanitize to create regexp
          .map(v => new RegExp(v, "i")) // create regexp
          .map(re => values.some(v => re.test(v))) // test regexp against all asset values
          .filter(it => it === false) // don't care about success
          .length;
        if (searchFailures) return false;
      }

      return true;
    });

    this.maxPages = Math.ceil(this.assetsFiltered.length / (+this.pageSize));

    this.assetsDisplay = [
      ...(this.assetsFiltered.slice(start, end)),
    ];
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }
    this.loadAssetsToDisplay();
  }

  setSelection(id: string, description: string) {
    this.assetSelectedData.id = id;
    this.assetSelectedData.description = description;
    if (id !== this.solution.assetId) this.selected = true;
    else this.selected = false;
  }

  @handleErrors()
  async unlinkSolution() {
    if (this.solution.type === "software") await this.assetService.unlinkSoftwareAsset(this.solution.id);
    else if (this.solution.type === "accessory") await this.assetService.unlinkAccessory(this.solution.id);
    else this.close();

    this.result = {
      success: true,
      data: {id: "", description: ""},
    };
    this.close();
  }

  @handleErrors()
  async linkSolution() {
    await this.assetService.linkAccessoriesSoftwareAsset(
      this.assetSelectedData.id,
      this.typeModal,
      [this.solution],
    );

    this.result = {
      success: true,
      data: this.assetSelectedData,
    };
    this.close();
  }

  @handleErrors()
  async saveSelection(): Promise<void> {
    if (!this.assetSelectedData.id) await this.unlinkSolution();
    else await this.linkSolution();
  }

  constructor(
    public dialogService: DialogService,
    public assetService: AssetService,
    public accessoryService: AccessoryService,
  ) {
    super(dialogService);
  }
}
